<template>
  <div
    class="MyMapItemPopup"
    :class="{ 'MyMapItemPopup--resource': myMapStore.activeItemType === ItemType.RESOURCE }"
    ref="active-item-card"
  >
    <MyMapProjectPopupContent
      v-if="popupProject != null"
      :project="popupProject"
    />
    <MyMapResourcePopupContent
      v-else-if="popupResource != null"
      :resource="popupResource"
    />
    <ActorCard
      v-else-if="popupActor != null"
      :actor="popupActor"
      class="ActorCard ActorCard--light"
    />
    <div
      v-else-if="myMapStore.activeItemId != null && myMapStore.activeItemLoading"
      class="MyMapItemPopup__pending MyMapItemPopup__pending--loading"
    >
      <Icon icon="eos-icons:loading" class="MyMapItemPopup__pendingIcon" :size="28" />
      <span>{{ $t('myMap.itemPopup.loadingDetails') }}</span>
    </div>
    <div
      v-else-if="myMapStore.activeItemId != null"
      class="MyMapItemPopup__pending MyMapItemPopup__pending--error"
    >
      <p class="MyMapItemPopup__pendingMessage">{{ $t('myMap.itemPopup.loadFailed') }}</p>
      <button type="button" class="MyMapItemPopup__pendingClose" @click="dismissFailedPopup">
        {{ $t('myMap.itemPopup.close') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import MapComponent from '@/components/map/Map.vue'
import Icon from '@/components/ui/Icon.vue'
import { useMyMapStore } from '@/stores/myMapStore'
import MyMapProjectPopupContent from '@/views/map/components/MyMapProjectPopupContent.vue'
import { computed, nextTick, unref, useTemplateRef, watch, type Ref } from 'vue'
import ActorCard from '@/views/actors/components/ActorCard.vue'
import { ItemType } from '@/models/enums/app/ItemType'
import type { Project } from '@/models/interfaces/Project'
import type { Resource } from '@/models/interfaces/Resource'
import MyMapResourcePopupContent from '@/views/map/components/MyMapResourcePopupContent.vue'
import type { Actor } from '@/models/interfaces/Actor'
import MapService from '@/services/map/MapService'
import type { Item } from '@/models/interfaces/Item'
import type { Map as MapLibreMap } from 'maplibre-gl'

type MapComponentExposed = InstanceType<typeof MapComponent>

const myMapStore = useMyMapStore()
const activeItemCard = useTemplateRef('active-item-card')

/**
 * defineExpose refs stay as Ref when read from parent script — must unref.
 * Computed + try/catch avoids scheduler/HMR crashes when the Map instance is torn down.
 */
const mapLibreMapForPopup = computed((): MapLibreMap | null => {
  try {
    const comp = myMapStore.myMap as MapComponentExposed | undefined
    if (comp == null) return null
    const raw = (comp as { map?: unknown }).map
    if (raw == null) return null
    return unref(raw as Ref<MapLibreMap | null>) ?? null
  } catch {
    return null
  }
})

const activeFeatureIdFromMap = computed((): string | null => {
  try {
    const comp = myMapStore.myMap as MapComponentExposed | undefined
    if (comp == null) return null
    const raw = (comp as { activeFeatureId?: unknown }).activeFeatureId
    if (raw == null) return null
    return unref(raw as Ref<string | null>) ?? null
  } catch {
    return null
  }
})

const popupProject = computed((): Project | null =>
  myMapStore.activeItemType === ItemType.PROJECT && myMapStore.activeItem != null
    ? (myMapStore.activeItem as Project)
    : null,
)
const popupResource = computed((): Resource | null =>
  myMapStore.activeItemType === ItemType.RESOURCE && myMapStore.activeItem != null
    ? (myMapStore.activeItem as Resource)
    : null,
)
const popupActor = computed((): Actor | null =>
  myMapStore.activeItemType === ItemType.ACTOR && myMapStore.activeItem != null
    ? (myMapStore.activeItem as Actor)
    : null,
)

const myMapComponent = computed(() => myMapStore.myMap as MapComponentExposed | undefined)

async function registerMapClickPopups(): Promise<void> {
  try {
    const mapInstance = mapLibreMapForPopup.value
    if (mapInstance == null) return
    await MapService.isLoaded(mapInstance, () => addPopupOnClick())
    await showPopupOnInit()
  } catch (err) {
    console.warn('[MyMapItemPopup] registerMapClickPopups failed', err)
  }
}

watch(
  [
    () => myMapStore.myMap,
    mapLibreMapForPopup,
    () => myMapStore.activeItem,
    () => myMapStore.isMapAlreadyBeenMounted,
  ],
  () => {
    void registerMapClickPopups()
  },
  { flush: 'post', immediate: true },
)

watch(
  activeFeatureIdFromMap,
  (to, from) => {
    void nextTick(() => {
      try {
        console.log('[MyMapItemPopup] activeFeatureId changed', { from, to })
        if (myMapComponent.value == null) {
          console.warn('[MyMapItemPopup] activeFeatureId watch: map component ref is null')
          return
        }
        if (to == null || to === '') {
          myMapStore.activeItemId = null
          console.log('[MyMapItemPopup] cleared activeItemId (popup closed or no selection)')
          return
        }
        myMapStore.activeItemId = String(to)
        console.log('[MyMapItemPopup] set activeItemId', myMapStore.activeItemId)
      } catch (err) {
        console.warn('[MyMapItemPopup] activeFeatureId sync failed', err)
      }
    })
  },
  { flush: 'post' },
)

const addPopupOnClick = () => {
  const comp = myMapComponent.value
  if (comp == null) {
    console.warn('[MyMapItemPopup] addPopupOnClick: map component is null')
    return
  }
  const cardEl = activeItemCard.value
  if (cardEl == null) {
    console.warn('[MyMapItemPopup] addPopupOnClick: active-item-card template ref is null')
  }
  Object.values(ItemType).forEach((itemLayer) => {
    comp.addPopupOnClick(itemLayer, cardEl, false)
  })
  console.log('[MyMapItemPopup] addPopupOnClick registered handlers for ItemType layers', {
    hasCardEl: cardEl != null,
  })
}

const showPopup = () => {
  const comp = myMapComponent.value
  if (comp == null) return
  Object.values(ItemType).forEach((itemType) => {
    comp.addPopupOnClick(itemType, activeItemCard.value, false)
  })
  if (mapLibreMapForPopup.value != null && myMapStore.activeItem != null) {
    if ((myMapStore.activeItem as Item)?.geoData?.coords) {
      comp.addPopup(
        (myMapStore.activeItem as Item)?.geoData?.coords,
        activeItemCard.value,
        false,
      )
    }
  }
}

const showPopupOnInit = async () => {
  const mapInstance = mapLibreMapForPopup.value
  if (mapInstance == null) return
  await MapService.isLoaded(mapInstance, () => {
    addPopupOnClick()
    showPopup()
  })
}

function dismissFailedPopup(): void {
  myMapStore.myMap?.closeActivePopup?.()
}
</script>

<style scoped lang="scss">
.MyMapItemPopup__pending {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-width: 12rem;
  padding: 1.25rem 1rem;
  border-radius: 10px;
  border: 1px solid rgb(var(--v-theme-main-blue));
  background: #fff;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  color: rgb(var(--v-theme-main-blue));
  font-size: 0.875rem;
  font-weight: 500;
}

.MyMapItemPopup__pending--loading {
  flex-flow: row nowrap;
}

.MyMapItemPopup__pendingIcon {
  flex-shrink: 0;
  color: rgb(var(--v-theme-main-blue));
}

.MyMapItemPopup__pendingMessage {
  margin: 0;
  text-align: center;
  line-height: 1.4;
  color: #374151;
}

.MyMapItemPopup__pendingClose {
  padding: 0.35rem 0.85rem;
  border: 1px solid rgb(var(--v-theme-main-blue));
  border-radius: 6px;
  background: #fff;
  color: rgb(var(--v-theme-main-blue));
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.MyMapItemPopup__pendingClose:hover {
  background: rgba(var(--v-theme-main-blue), 0.08);
}
</style>

<style lang="scss">
.maplibregl-popup:not(.show),
.maplibregl-popup:not(.show) * {
  pointer-events: none !important;
}
.MyMap {
  width: 100%;
  height: 100%;
  position: relative;

  .maplibregl-popup,
  .MyMapItemPopup .v-card {
    max-width: 22rem !important;
  }

  .MyMap__map {
    width: 100%;
    height: 100%;

    .maplibregl-ctrl-top-right {
      align-items: flex-end;
    }
  }
}
</style>
